import { useState, useCallback, useEffect } from 'react';
import { Alert, Platform } from 'react-native';
import { fetchNotifications, markNotificationAsRead, deleteNotification, createChannel } from '@/services/universe/client';
import { NotificationItem } from '@/services/universe/types';
import { useNotification } from '@/services/notifications/provider';
import { useRouter } from 'expo-router';
import * as Haptics from 'expo-haptics';
import { useIsTablet } from '@/hooks/useIsTablet';
import { navigationEvents, NAVIGATION_EVENT_TYPES } from '@/utils/navigationEvents';

export function useNotificationsManager() {
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [errorModal, setErrorModal] = useState<{ visible: boolean, message: string, notificationId: string | null }>({
        visible: false,
        message: '',
        notificationId: null
    });
    const { notification: pushNotification, setUnreadCount } = useNotification();
    const router = useRouter();
    const isTablet = useIsTablet();

    const loadNotifications = useCallback(async (isRefresh = false, targetPage = 1) => {
        if (isRefresh) {
            setIsRefreshing(true);
        } else if (targetPage > 1) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            const response = await fetchNotifications(targetPage);
            if (response.success) {
                if (targetPage === 1) {
                    setNotifications(response.data);
                } else {
                    setNotifications(prev => [...prev, ...response.data]);
                }
                
                const meta = response.meta;
                setPage(meta.current_page);
                setHasMore(meta.current_page < meta.last_page);
                
                if (typeof meta.unread_count === 'number') {
                    setUnreadCount(meta.unread_count);
                }
            }
        } catch (error) {
            console.error('Failed to load notifications:', error);
        } finally {
            setIsLoading(false);
            setIsRefreshing(false);
            setIsLoadingMore(false);
        }
    }, []);

    const handleRefresh = useCallback(() => {
        setHasMore(true);
        loadNotifications(true, 1);
    }, [loadNotifications]);

    const handleLoadMore = useCallback(() => {
        if (!isLoadingMore && hasMore) {
            loadNotifications(false, page + 1);
        }
    }, [isLoadingMore, hasMore, loadNotifications, page]);

    const handleNotificationPress = useCallback(async (item: NotificationItem, onBeforeRedirect?: () => void) => {
        const type = item.additional_data?.type;
        const inscriptionId = item.additional_data?.inscription_id;
        const isGuideFinder = item.additional_data?.is_guide_finder;

        if (!item.read_at) {
            const response = await markNotificationAsRead(item.id);
            if (response && response.success !== false) {
                if (typeof response.data?.unread_count === 'number') {
                    setUnreadCount(response.data.unread_count);
                }
                setNotifications(prev => 
                    prev.map(notif => 
                        notif.id === item.id 
                            ? { ...notif, read_at: new Date().toISOString() } 
                            : notif
                    )
                );
            } else if (response && response.success === false) {
                console.error('Failed to mark notification as read:', response.message);
            }
        }

        if (type === 'new_lead' && inscriptionId) {
            if (onBeforeRedirect) onBeforeRedirect();
            setIsLoading(true);
            const response = await createChannel(inscriptionId, Boolean(isGuideFinder));
            
            if (response && response.success !== false && response.channel && response.channel.id) {
                if (isTablet) {
                    router.replace(`/?channelId=${response.channel.id}`);
                } else {
                    router.push(`/chat/${response.channel.id}`);
                }
            } else if (response && response.success === false) {
                //console.error('Failed to create channel from notification list:', response.message);
                setErrorModal({
                    visible: true,
                    message: response.message || 'An unexpected error occurred',
                    notificationId: item.id
                });
            }
            setIsLoading(false);
        }
    }, [router, isTablet]);

    const closeErrorModal = useCallback(() => {
        setErrorModal(prev => ({ ...prev, visible: false }));
    }, []);

    const handleDelete = useCallback(async (id: string) => {
        Haptics.notificationAsync(Haptics.NotificationFeedbackType.Success);
        
        const previousNotifications = [...notifications];
        setNotifications(prev => prev.filter(n => n.id !== id));

        const response = await deleteNotification(id);
        if (response && response.success !== false) {
            // Success, already filtered from UI
            if (typeof response.data?.unread_count === 'number') {
                setUnreadCount(response.data.unread_count);
            }
        } else {
            setNotifications(previousNotifications);
            console.error('Failed to delete notification:', response?.message || 'Unknown error');
        }
    }, [notifications]);

    const handleDeleteFromModal = useCallback(async () => {
        if (errorModal.notificationId) {
            const id = errorModal.notificationId;
            closeErrorModal();
            await handleDelete(id);
        }
    }, [errorModal.notificationId, handleDelete, closeErrorModal]);

    // Auto-refresh when a new push notification arrives
    useEffect(() => {
        if (pushNotification) {
            handleRefresh();
        }
    }, [pushNotification, handleRefresh]);

    return {
        notifications,
        isLoading,
        isRefreshing,
        isLoadingMore,
        hasMore,
        errorModal,
        handleRefresh,
        handleLoadMore,
        handleNotificationPress,
        handleDelete,
        loadNotifications,
        closeErrorModal,
        handleDeleteFromModal
    };
}
